import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

// How often to ping the backend (ms). ~60s is enough for online/offline UX.
const HEARTBEAT_MS = 60 * 1000;

/**
 * Fires a page-view heartbeat to the backend whenever a signed-in staff/intern
 * navigates. Also pings every 60s to maintain "online" status. Silent on failure.
 */
export default function StaffHeartbeat() {
  const { user, isAuthenticated, api } = useAuth();
  const location = useLocation();
  const lastPathRef = useRef(null);

  const isStaff = !!(user && (user.is_admin || user.is_staff || user.intern_role));

  // Ping on route change
  useEffect(() => {
    if (!isAuthenticated || !isStaff) return;
    const path = location.pathname + (location.search || '');
    if (path === lastPathRef.current) return;
    lastPathRef.current = path;
    api.post('/staff/heartbeat', { path, event: 'page_view' }).catch(() => {});
  }, [location.pathname, location.search, isAuthenticated, isStaff, api]);

  // Keep-alive ping every minute
  useEffect(() => {
    if (!isAuthenticated || !isStaff) return;
    const send = () => {
      const path = location.pathname + (location.search || '');
      api.post('/staff/heartbeat', { path, event: 'keepalive' }).catch(() => {});
    };
    const t = setInterval(send, HEARTBEAT_MS);
    return () => clearInterval(t);
  }, [isAuthenticated, isStaff, api, location]);

  return null;
}
